import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { publicApi } from "@/lib/api";
import { Loader2, MapPin, Clock, ArrowLeft } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1551529563-fce9529e67ac?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function DemoShop() {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicApi.get("/public/tenant/abccoffee").then((t) => setTenant(t.data)).finally(() => setLoading(false));

    // inject the MorrCart embed exactly as a real host site would
    if (!document.getElementById("morrcart-embed")) {
      const s = document.createElement("script");
      s.id = "morrcart-embed";
      s.src = "/embed.js";
      s.setAttribute("data-tenant", "abccoffee");
      s.setAttribute("data-api", BACKEND);
      document.body.appendChild(s);
    }
    return () => {
      const host = document.querySelector("[data-morrcart]");
      if (host) host.remove();
      const s = document.getElementById("morrcart-embed");
      if (s) s.remove();
      const css = document.getElementById("morrcart-catalog-css");
      if (css) css.remove();
      delete window.MorrCart;
      window.__morrcartLoaded = false;
    };
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F5F2EB]"><Loader2 className="h-6 w-6 animate-spin text-[#8C5A46]" /></div>;

  return (
    <div className="min-h-screen bg-[#F5F2EB] text-[#2C2A26] font-demo-body">
      {/* Host-authored CSS styling MorrCart's product catalog — content from MorrCart, style from host */}
      <style>{`
        .mc-catalog{ gap:32px; }
        .mc-cat-title{ font-family:'Cormorant Garamond',serif; font-size:28px; font-weight:600; color:#2C2A26; border-bottom:1px solid #e4ddce; padding-bottom:6px; }
        .mc-product{ background:#fff; border:none; border-radius:10px; box-shadow:0 1px 3px rgba(0,0,0,.06); transition:box-shadow .2s ease; }
        .mc-product:hover{ box-shadow:0 8px 24px rgba(0,0,0,.10); }
        .mc-product-img{ height:180px; }
        .mc-product-name{ font-family:'Cormorant Garamond',serif; font-size:22px; }
        .mc-price{ color:#8C5A46; font-family:'Work Sans',sans-serif; }
        .mc-product-desc{ font-family:'Work Sans',sans-serif; }
        .mc-add-btn{ background:#8C5A46; border-radius:999px; letter-spacing:.03em; }
        .mc-add-btn:hover{ background:#734734; }
      `}</style>

      <div className="bg-[#2C2A26] text-[#F5F2EB] text-xs sm:text-sm py-2 px-4 flex items-center justify-center gap-2 text-center">
        <span>This is a sample host website — the menu, cart & portal are all MorrCart (product content), styled by this site.</span>
        <Link to="/" className="underline inline-flex items-center gap-1"><ArrowLeft className="h-3 w-3" /> MorrCart</Link>
      </div>

      <header className="border-b border-[#e4ddce]">
        <div className="max-w-5xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="font-serif-demo text-2xl font-semibold tracking-wide">{tenant?.name}</div>
          <nav className="hidden sm:flex gap-8 text-sm font-medium items-center">
            <span>Menu</span><span>Our Story</span><span>Visit</span>
            <a href="#" data-morrcart-account className="underline decoration-[#8C5A46] underline-offset-4 cursor-pointer" data-testid="demo-my-orders">My Account / Login</a>
          </nav>
        </div>
      </header>

      <section className="relative">
        <img src={HERO} alt="cafe" className="w-full h-[42vh] object-cover" />
        <div className="absolute inset-0 bg-black/30 flex items-center">
          <div className="max-w-5xl mx-auto px-6 w-full text-white">
            <h1 className="font-serif-demo text-4xl sm:text-6xl font-semibold max-w-lg leading-tight">Slow-crafted coffee, made your way.</h1>
            <div className="mt-4 flex flex-wrap gap-6 text-sm">
              <span className="flex items-center gap-2"><Clock className="h-4 w-4" /> {tenant?.business_hours}</span>
              <span className="flex items-center gap-2"><MapPin className="h-4 w-4" /> 24 Roasters Lane</span>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="font-serif-demo text-3xl sm:text-4xl font-semibold text-center">{tenant?.catalog_heading || "Our Menu"}</h2>
        <p className="text-center text-[#6b6154] mt-2">Tap an item to add it to your order.</p>
        {/* MorrCart injects products here (light DOM); host CSS above styles them */}
        <div className="mt-10" data-morrcart-products data-group-by="category" data-testid="demo-catalog" />
      </section>

      <footer className="border-t border-[#e4ddce] py-8 text-center text-sm text-[#6b6154]">
        {tenant?.name} · abccoffee.morrcart.com
      </footer>
    </div>
  );
}
